import React from 'react';
import { Link } from 'react-router-dom';

const DashboardSelection = ({ user, onLogout }) => {
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
          <Link to="/login" className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <img 
                src="/images/logo/MathVenture_logo.png" 
                alt="MathVenture Logo" 
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">Welcome, Admin!</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{user.username.split('@')[0].charAt(0).toUpperCase() + user.username.split('@')[0].slice(1)}</span>
              <button
                onClick={onLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Selection */}
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="max-w-4xl w-full">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Dashboard</h2>
            <p className="text-lg text-gray-600">
              As an admin, you have access to both user and administrative features
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Admin Dashboard */}
            <Link
              to="/admin-dashboard"
              className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border-2 border-transparent hover:border-purple-200"
            >
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-purple-100 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <span className="text-3xl">👑</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Admin Dashboard</h3>
                <p className="text-gray-600 mb-6">
                  Manage users, subscriptions, and view analytics. Full administrative control over the platform.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    User Management
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Subscription Control
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Analytics & Reports
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    System Monitoring
                  </div>
                </div>
                <div className="mt-6">
                  <span className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg group-hover:bg-purple-700 transition-colors">
                    Access Admin Panel
                    <span className="ml-2">→</span>
                  </span>
                </div>
              </div>
            </Link>

            {/* User Dashboard */}
            <Link
              to="/dashboard"
              className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border-2 border-transparent hover:border-blue-200"
            >
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <span className="text-3xl">🎓</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">User Dashboard</h3>
                <p className="text-gray-600 mb-6">
                  Experience the platform as a regular user. Play games, take quizzes, and track your progress.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Math Games & Quizzes
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Progress Tracking
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Leaderboards
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    All Grade Levels
                  </div>
                </div>
                <div className="mt-6">
                  <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg group-hover:bg-blue-700 transition-colors">
                    Enter Learning Mode
                    <span className="ml-2">→</span>
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="mt-12 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/subscription-manager"
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="mr-2">💳</span>
                Manage Subscriptions
              </Link>
              <Link
                to="/games"
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="mr-2">🎮</span>
                Browse Games
              </Link>
              <Link
                to="/leaderboard"
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="mr-2">🏆</span>
                View Leaderboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSelection;